import React, { useState } from 'react';
import { Phone, Mail, MapPin, Instagram, Facebook } from 'lucide-react';
import { businessInfo } from '../data/mock';
import { toast } from 'sonner';
import './Pages.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    applianceType: '',
    brand: '',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Send to backend API instead of EmailJS
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        toast.success(result.message);
        setFormData({
          name: '',
          email: '',
          phone: '',
          address: '',
          applianceType: '',
          brand: '',
          message: ''
        });
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      // Log error for debugging (consider using error tracking service in production)
      toast.error('Something went wrong. Please call 604-377-6407 directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const applianceTypes = [
    'Refrigerator/Freezer',
    'Dishwasher',
    'Range/Oven',
    'Cooktop',
    'Wine Cooler',
    'Microwave',
    'Other'
  ];

  const brands = [
    'Sub-Zero',
    'Wolf',
    'Gaggenau',
    'Miele',
    'Thermador',
    'Bosch',
    'Liebherr',
    'Other'
  ];

  return (
    <div className="page-contact">
      {/* Hero Section */}
      <section className="contact-hero section-padding-small">
        <div className="container">
          <h1 className="hero-medium">Request a Quote</h1>
          <p className="body-large space-top-md text-secondary">
            Tell Joe about your appliance installation project and receive a detailed quote within 24 hours
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section-padding" id="contact">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Form */}
            <div className="contact-form-container">
              <form onSubmit={handleSubmit} className="quote-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="Your full name"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="phone" className="form-label">Phone *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      data-testid="contact-form-phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="form-input"
                      placeholder="(604) 555-0123"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    data-testid="contact-form-email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="address" className="form-label">Installation Address *</label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="City or full address"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="applianceType" className="form-label">Appliance Type *</label>
                    <select
                      id="applianceType"
                      name="applianceType"
                      value={formData.applianceType}
                      onChange={handleChange}
                      required
                      className="form-select"
                    >
                      <option value="">Select appliance type</option>
                      {applianceTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="brand" className="form-label">Brand *</label>
                    <select
                      id="brand"
                      name="brand"
                      value={formData.brand}
                      onChange={handleChange}
                      required
                      className="form-select"
                    >
                      <option value="">Select brand</option>
                      {brands.map((brand) => (
                        <option key={brand} value={brand}>{brand}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">Project Details</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    className="form-textarea"
                    placeholder="Tell us about your installation project. Include any specific requirements or questions."
                  />
                </div>

                <button 
                  type="submit" 
                  className="btn-primary btn-full-width"
                  data-testid="contact-form-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Request Quote'}
                </button>

                <p className="form-note body-small text-secondary space-top-md">
                  * Required fields. Joe typically responds within 24 hours during business days.
                </p>
              </form>
            </div>

            {/* Contact Information */}
            <div className="contact-info-container">
              <div className="contact-info-card">
                <h3 className="heading-2">Get In Touch</h3>
                <p className="body-regular text-secondary space-top-md">
                  Prefer to call? Joe is happy to discuss your project and provide an estimate over the phone.
                </p>

                <div className="contact-methods space-top-xl">
                  <a href={`tel:${businessInfo.phone}`} className="contact-method">
                    <div className="contact-method-icon">
                      <Phone size={24} />
                    </div>
                    <div>
                      <h4 className="body-large">Phone</h4>
                      <p className="body-regular">{businessInfo.phone}</p>
                    </div>
                  </a>

                  <a href={`mailto:${businessInfo.email}`} className="contact-method">
                    <div className="contact-method-icon">
                      <Mail size={24} />
                    </div>
                    <div>
                      <h4 className="body-large">Email</h4>
                      <p className="body-regular">{businessInfo.email}</p>
                    </div>
                  </a>

                  <div className="contact-method">
                    <div className="contact-method-icon">
                      <MapPin size={24} />
                    </div>
                    <div>
                      <h4 className="body-large">Service Area</h4>
                      <p className="body-regular">{businessInfo.serviceArea}</p>
                    </div>
                  </div>
                </div>

                <div className="contact-social space-top-xl">
                  <h4 className="body-large">Follow Us</h4>
                  <div className="social-links space-top-md">
                    <a href={businessInfo.instagram} target="_blank" rel="noopener noreferrer" className="social-link">
                      <Instagram size={24} />
                      <span>Instagram</span>
                    </a>
                    <a href={businessInfo.facebook} target="_blank" rel="noopener noreferrer" className="social-link">
                      <Facebook size={24} />
                      <span>Facebook</span>
                    </a>
                  </div>
                </div>
              </div>

              <div className="business-hours-card space-top-lg">
                <h4 className="heading-3">Business Information</h4>
                <ul className="business-info-list space-top-md">
                  <li className="body-small">{businessInfo.insurance}</li>
                  <li className="body-small">{businessInfo.worksafe}</li>
                  <li className="body-small">{businessInfo.gst}</li>
                  <li className="body-small">{businessInfo.experience}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Response Time Notice */}
      <section className="section-padding-small response-notice-section">
        <div className="container">
          <div className="response-notice">
            <h3 className="heading-2">What Happens Next?</h3>
            <div className="response-steps space-top-lg">
              <div className="response-step">
                <span className="step-number">1</span>
                <div>
                  <h4 className="body-large">Joe Reviews Your Request</h4>
                  <p className="body-small text-secondary">Detailed assessment of your installation needs</p>
                </div>
              </div>
              <div className="response-step">
                <span className="step-number">2</span>
                <div>
                  <h4 className="body-large">Contact Within 24 Hours</h4>
                  <p className="body-small text-secondary">Phone or email with initial quote and questions</p>
                </div>
              </div>
              <div className="response-step">
                <span className="step-number">3</span>
                <div>
                  <h4 className="body-large">Schedule Your Installation</h4>
                  <p className="body-small text-secondary">Book a convenient time for professional installation</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
